import { ReactNode } from 'react'
import {
  chakra,
  CSSObject,
  Tag,
  TagCloseButton,
  BoxProps,
  FormErrorMessage,
  FormControl,
  FormLabel,
} from '@ritualco/jutsu'
import { useField } from 'formik'
import { ReactMultiEmail } from 'react-multi-email'
import 'react-multi-email/style.css'

const style: CSSObject = {
  // Mimics Jutsu Input styles
  cursor: 'text',
  borderRadius: 'lg',
  borderColor: 'grayscale.quinary',
  maxHeight: '10em',
  overflow: 'auto',
  _focusWithin: {
    borderColor: 'teal.darker',
  },
  '&.empty > span[data-placeholder]': {
    fontSize: 'medium',
    top: '14px',
    left: 3,
    padding: 0,
    color: 'grayscale.tertiary',
  },
  '> input': {
    fontSize: 'medium',
    color: 'grayscale.primary',
    pl: 3,
    width: '25em !important',
  },
}

const ChakraReactMultiEmail = chakra(ReactMultiEmail as any, {
  baseStyle: style,
})

export type FormikMultiEmailInputProps = {
  placeholder?: ReactNode
  label?: string
  name: string
  isRequired?: boolean
} & BoxProps

export const FormikMultiEmailInput = ({
  label = '',
  placeholder = '',
  isRequired = false,
  ...props
}: FormikMultiEmailInputProps) => {
  const [field, { error, touched }, helpers] = useField(props.name)
  const isInvalid = !!error && touched
  const sx: CSSObject = isInvalid
    ? {
        borderColor: 'red.normal',
      }
    : {}

  return (
    <FormControl
      id={props.name}
      isInvalid={isInvalid}
      isRequired={isRequired}
      mb={6}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <div onBlur={() => helpers.setTouched(true, false)}>
        <ChakraReactMultiEmail
          placeholder={placeholder}
          emails={field.value}
          onChange={(_emails: string[]) => helpers.setValue(_emails)}
          isInvalid={isInvalid}
          sx={sx}
          getLabel={(
            email: string,
            index: number,
            removeEmail: (index: number) => void
          ) => (
            <Tag
              key={index}
              borderRadius="full"
              size="lg"
              fontSize="small"
              fontWeight="normal"
              backgroundColor="grayscale.greyFill"
              color="grayscale.primary"
              mr={2}
            >
              {email}
              <TagCloseButton onClick={() => removeEmail(index)} />
            </Tag>
          )}
          {...props}
        />
      </div>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  )
}
